/*
 * Debug utilities extracted from shopify-utils.js
 * Provides: debugLog, enableDebugMode, disableDebugMode, reloadMainScript
 * and automatically exposes them under `window.discoDebug` for use from DevTools.
 */

// Enhanced debug logging that works in environments where you don't control the page.
// Supports multiple debug triggers: URL params, localStorage, and the existing window.disco.DEBUG
//
// Usage examples:
//   debugLog('Hello');
//   debugLog({ some: 'object' });
export function debugLog(msg, level = 'log') {
  const isDebugEnabled =
    new URLSearchParams(window.location.search).has('debug') ||
    new URLSearchParams(window.location.search).has('disco_debug') ||
    localStorage.getItem('disco_debug') === 'true' ||
    (window.disco && window.disco.DEBUG);

  if (!isDebugEnabled) return;

  if (typeof msg === 'object' && msg !== null) {
    switch (level) {
      case 'warn':
        console.warn(msg);
        break;
      case 'error':
        console.error(msg);
        break;
      case 'info':
        console.info(msg);
        break;
      default:
        console.log(msg);
    }
    return;
  }

  const prefix = `[Disco Debug ${new Date().toISOString()}]`;
  const text = `${prefix} ${msg}`;
  switch (level) {
    case 'warn':
      console.warn(text);
      break;
    case 'error':
      console.error(text);
      break;
    case 'info':
      console.info(text);
      break;
    default:
      console.log(text);
  }
}

// Enable/disable helpers ----------------------------------------------------
export function enableDebugMode() {
  localStorage.setItem('disco_debug', 'true');
  console.log('[Disco] Debug mode enabled. Reload the page to see debug logs.');
}

export function disableDebugMode() {
  localStorage.removeItem('disco_debug');
  console.log('[Disco] Debug mode disabled. Reload the page to stop debug logs.');
}

// Script reload helper -------------------------------------------------------
/**
 * Reloads a JS bundle on the page (defaults to main.js from productessentials.app).
 *
 * Example DevTools usage:
 *   discoDebug.reload(); // reload main.js from productessentials.app
 *   discoDebug.reload('flash-sale');
 *   discoDebug.reload('main', 'staging.productessentials.app');
 */
export function reloadMainScript(identifier = 'main', host = 'productessentials.app') {
  const scripts = Array.from(document.getElementsByTagName('script'));
  const target = scripts.find((s) => {
    if (!s.src) return false;
    const url = new URL(s.src, window.location.origin);
    return url.hostname.includes(host) && url.pathname.includes(identifier) && url.pathname.endsWith('.js');
  });

  if (!target) {
    console.warn(`[Disco] reloadMainScript: Could not find script from "${host}" containing "${identifier}".`);
    return;
  }

  const fresh = new URL(target.src);
  fresh.searchParams.set('cb', Date.now().toString());

  const newScript = document.createElement('script');
  newScript.src = fresh.toString();
  newScript.type = target.type || 'text/javascript';
  newScript.async = target.async;
  newScript.defer = target.defer;
  newScript.onload = () => console.log(`[Disco] Script reloaded -> ${newScript.src}`);

  target.parentNode.insertBefore(newScript, target.nextSibling);
}

// ---------------------------------------------------------------------------
function exposeDebugCommands() {
  if (typeof window === 'undefined') return;

  window.discoDebug = {
    enable: enableDebugMode,
    disable: disableDebugMode,
    log: debugLog,
    reload: reloadMainScript,
    status() {
      const enabled =
        new URLSearchParams(window.location.search).has('debug') ||
        new URLSearchParams(window.location.search).has('disco_debug') ||
        localStorage.getItem('disco_debug') === 'true' ||
        (window.disco && window.disco.DEBUG);
      console.log(`[Disco] Debug mode: ${enabled ? 'ENABLED' : 'DISABLED'}`);
      console.log('Available commands:\n  discoDebug.enable()\n  discoDebug.disable()\n  discoDebug.log(msg)\n  discoDebug.reload([identifier], [host])');
      return enabled;
    },
  };

  // Auto-print help if debug already enabled
  if (localStorage.getItem('disco_debug') === 'true') {
    console.log('[Disco] Debug mode is enabled. Use discoDebug.status() for help.');
  }
}

exposeDebugCommands(); 